import React, { useState } from 'react';
import {
  BackgroundCardWrapper,
  BackgroundName,
  CardImage,
  CardImageContainer,
  DeleteIconWrapper,
  SystemDefaultWrapper,
} from './styles';
import { Button, StatusBadge } from 'react-covideo-common';
import { Flex } from 'lib/components/styles/layout';
import { IoCheckmark } from 'react-icons/io5';
import { useTheme } from 'styled-components/macro';
import { IBackgroundResponse } from 'lib/api/backgrounds/useQueryBackgrounds';
import { MdDeleteForever } from 'react-icons/md';
import { DeleteBackgroundModal } from './modals/DeleteBackgroundModal';

interface BackgroundCardProps {
  background: IBackgroundResponse;
  onDefaultBackgroundChange: (backgroundId: number) => void;
  isLoading: boolean;
}

export const BackgroundCard = ({
  background,
  onDefaultBackgroundChange,
  isLoading,
}: BackgroundCardProps) => {
  const theme = useTheme();
  const {
    fileName,
    default: isDefaultBackground,
    backgroundId,
    customerId,
  } = background;

  // System generated backgrounds have null customerId
  const isSystemGeneratedBackground = customerId === null;

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const toggleShowDeleteModal = () => {
    setShowDeleteModal(prev => !prev);
  };

  const buttonText = isDefaultBackground
    ? 'Default Background'
    : 'Set As Default Background';
  const buttonVariant = isDefaultBackground ? 'passive' : 'white';
  const buttonIcon = isDefaultBackground ? (
    <IoCheckmark size={20} color={theme.colors.white[80]} />
  ) : undefined;

  const isProccessing = isLoading;
  const canDelete = customerId !== null; // only custom backgrounds can be deleted

  return (
    <BackgroundCardWrapper default={isDefaultBackground}>
      {!isProccessing && canDelete && (
        <DeleteIconWrapper onClick={toggleShowDeleteModal}>
          <MdDeleteForever size={24} color={theme.colors.danger[100]} />
        </DeleteIconWrapper>
      )}
      {showDeleteModal && (
        <DeleteBackgroundModal
          background={background}
          onClose={toggleShowDeleteModal}
        />
      )}
      <Flex flexDirection='column' gap='10px'>
        <CardImageContainer>
          <CardImage src={background.url} />
          {isSystemGeneratedBackground && (
            <SystemDefaultWrapper>
              <StatusBadge
                text='System Generated'
                size='medium'
                extendStyles={{
                  container: {
                    backgroundColor: theme.colors.secondary['non_alpha'],
                    border: `1px solid ${theme.colors.secondary[5]}`,
                    borderRadius: '6px',
                  },
                  text: {
                    color: theme.colors.secondary[80],
                    fontWeight: 500,
                  },
                }}
              />
            </SystemDefaultWrapper>
          )}
        </CardImageContainer>
        <Flex
          flexDirection='column'
          padding='0 16px 16px 16px'
          gap='16px'
          flexGrow={1}
          justifyContent='space-between'
          width='auto'
        >
          <BackgroundName title={fileName}>{fileName}</BackgroundName>
          <Button
            variant={buttonVariant}
            size='small'
            text={buttonText}
            icon={buttonIcon}
            disabled={isProccessing}
            onClick={() => onDefaultBackgroundChange(backgroundId)}
            extendStyles={{
              text: {
                fontWeight: 400,
              },
            }}
          />
        </Flex>
      </Flex>
    </BackgroundCardWrapper>
  );
};
